import React, { useState, useRef } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import './ProgramImportTool.css';
import Papa from 'papaparse';

function ProgramImportTool() {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseFile(selectedFile);
    }
  };
  
  const parseFile = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        console.log("Parsed data:", results);
        setParsedData(results.data);
      },
      error: (error) => {
        console.error("Parsing error:", error);
        alert(`Error parsing file: ${error.message}`);
      }
    });
  };
  
  const processImport = async () => {
    if (!parsedData || parsedData.length === 0) {
      alert("No data to import");
      return;
    }
    
    setProcessing(true);
    setResults(null);
    
    try {
      const studentsToUpdate = [];
      
      // Prepare student data from CSV
      // Assuming CSV has columns: StudentID, Name, Program
      for (const row of parsedData) {
        const studentId = row.StudentID?.trim();
        const program = row.Program?.trim();
        
        if (studentId && program) {
          // Check if this student is already in our list
          const existingIndex = studentsToUpdate.findIndex(s => s.id === studentId);
          
          if (existingIndex >= 0) {
            // Add to existing student's programs
            if (!studentsToUpdate[existingIndex].programs.includes(program)) {
              studentsToUpdate[existingIndex].programs.push(program);
            }
          } else {
            // Add new student entry
            studentsToUpdate.push({
              id: studentId,
              name: row.Name?.trim() || '',
              programs: [program]
            });
          }
        }
      }
      
      console.log("Students to update:", studentsToUpdate);
      
      // Update students in Firestore
      let successCount = 0;
      let errorCount = 0;
      let notFoundCount = 0;
      const errors = [];
      
      for (const student of studentsToUpdate) {
        try {
          // Get current student data
          const studentRef = doc(db, "students", student.id);
          const studentSnap = await getDoc(studentRef);
          
          if (studentSnap.exists()) {
            const studentData = studentSnap.data();
            
            // Prepare programs array
            let programs = [];
            
            // Add existing program if it exists
            if (studentData.program) {
              programs.push(studentData.program);
            }
            
            // Add existing programs array if it exists
            if (Array.isArray(studentData.programs)) {
              programs = [...programs, ...studentData.programs];
            }
            
            // Add new programs
            student.programs.forEach(program => {
              if (!programs.includes(program)) {
                programs.push(program);
              }
            });
            
            // Remove duplicates
            programs = [...new Set(programs)];
            
            // Update student record
            await updateDoc(studentRef, {
              programs: programs,
              // Keep original program field for backward compatibility
              program: studentData.program || programs[0]
            });
            
            successCount++;
          } else {
            notFoundCount++;
            errors.push(`Student not found: ${student.id} (${student.name})`);
          }
        } catch (error) {
          console.error(`Error updating student ${student.id}:`, error);
          errorCount++;
          errors.push(`Error updating ${student.id}: ${error.message}`);
        }
      }
      
      setResults({
        total: studentsToUpdate.length,
        success: successCount,
        errors: errorCount,
        notFound: notFoundCount,
        errorMessages: errors
      });
    } catch (error) {
      console.error("Import error:", error);
      setResults({
        error: `Import failed: ${error.message}`
      });
    } finally {
      setProcessing(false);
    }
  };
  
  const resetImport = () => {
    setFile(null);
    setParsedData(null);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <div className="program-import-container">
      <h2>Import Multiple Programs</h2>
      <p className="description">
        Use this tool to update students with multiple programs of study. 
        Upload a CSV file with columns: StudentID, Name, Program.
      </p>
      
      <div className="csv-format-guide">
        <h3>CSV Format Guide</h3>
        <p>Your CSV file should have these exact column headers:</p>
        <pre>StudentID,Name,Program</pre>
        <p>Each row represents a student program. If a student has multiple programs, they should appear in multiple rows with the same StudentID.</p>
        <p>Example:</p>
        <pre>
StudentID,Name,Program
123456,John Smith,Computer Programming
123456,John Smith,Cybersecurity
789012,Jane Doe,Culinary Arts
        </pre>
      </div>
      
      <div className="import-form">
        <div className="file-input-container">
          <label htmlFor="program-csv">Upload CSV File:</label>
          <input
            type="file"
            id="program-csv"
            accept=".csv"
            onChange={handleFileChange}
            ref={fileInputRef}
          />
        </div>
        
        {parsedData && (
          <div className="parsed-data-summary">
            <h3>Found {parsedData.length} rows in the CSV</h3>
            <button 
              className="process-button"
              onClick={processImport}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Update Students'}
            </button>
            <button 
              className="reset-button"
              onClick={resetImport}
              disabled={processing}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      
      {results && (
        <div className="import-results">
          <h3>Import Results</h3>
          {results.error ? (
            <div className="error-message">{results.error}</div>
          ) : (
            <div className="results-summary">
              <p>Total students: {results.total}</p>
              <p className="success">Successfully updated: {results.success}</p>
              {results.notFound > 0 && (
                <p className="warning">Students not found: {results.notFound}</p>
              )}
              {results.errors > 0 && (
                <p className="error">Errors: {results.errors}</p>
              )}
              
              {results.errorMessages && results.errorMessages.length > 0 && (
                <div className="error-details">
                  <h4>Error Details:</h4>
                  <ul>
                    {results.errorMessages.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProgramImportTool;